import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import { useToastContext } from '../App';

function Modpacks() {
  const [activeTab, setActiveTab] = useState('installed');
  const [modpacks, setModpacks] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [servers, setServers] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importLog, setImportLog] = useState([]);
  const [importProgress, setImportProgress] = useState(null);
  const addToast = useToastContext();

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      const [packs, srvs] = await Promise.all([
        api.getModpacks(),
        api.getServers(),
      ]);
      setModpacks(packs);
      setServers(srvs);
    } catch (e) {
      console.error(e);
    }
  }

  async function loadUploads() {
    try {
      const data = await api.getModpackUploads();
      setUploads(data);
    } catch (e) {
      console.error(e);
    }
  }

  async function handleImport(fileName) {
    if (importing) return;
    setImporting(true);
    setImportLog([]);
    setImportProgress(null);

    try {
      await api.importModpack(fileName, (event) => {
        setImportLog(prev => [...prev, event]);
        if (event.total) {
          setImportProgress({ current: event.current, total: event.total });
        }
        if (event.step === 'done') {
          addToast(`Modpack importiert!`, 'success');
          loadData();
        }
        if (event.step === 'error') {
          addToast(event.message, 'error');
        }
      });
    } catch (e) {
      addToast(e.message, 'error');
    } finally {
      setImporting(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Modpack wirklich loeschen? Alle heruntergeladenen Mods werden geloescht.')) return;
    try {
      await api.deleteModpack(id);
      addToast('Modpack geloescht', 'success');
      loadData();
    } catch (e) {
      addToast(e.message, 'error');
    }
  }

  function handleExport(id) {
    window.open(api.getModpackExportUrl(id), '_blank');
  }

  function formatSize(bytes) {
    if (!bytes) return '0 B';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  function formatRam(mb) {
    if (!mb) return '-';
    return mb >= 1024 ? `${Math.ceil(mb / 1024)} GB` : `${mb} MB`;
  }

  return (
    <div>
      <div className="page-header">
        <h2>Modpack-Verwaltung</h2>
      </div>

      <div className="tabs">
        <button className={`tab ${activeTab === 'installed' ? 'active' : ''}`} onClick={() => setActiveTab('installed')}>
          Installierte Modpacks ({modpacks.length})
        </button>
        <button className={`tab ${activeTab === 'import' ? 'active' : ''}`} onClick={() => { setActiveTab('import'); loadUploads(); }}>
          Importieren (/minecraft/upload)
        </button>
      </div>

      {activeTab === 'installed' && (
        <div>
          {modpacks.length === 0 ? (
            <div className="empty-state">
              <div className="icon">&#128230;</div>
              <p>Noch keine Modpacks installiert</p>
              <p className="text-sm text-muted">
                Lade ein CurseForge Modpack (.zip) in /minecraft/upload und importiere es hier.
              </p>
              <button className="btn btn-primary mt-4" onClick={() => { setActiveTab('import'); loadUploads(); }}>
                Modpack importieren
              </button>
            </div>
          ) : (
            <div className="grid grid-2">
              {modpacks.map(pack => {
                const server = servers.find(s => s.modpackId === pack.id);
                return (
                  <div key={pack.id} className="card">
                    <div className="card-header">
                      <h3>{pack.name} {pack.version && <span className="text-sm text-muted">v{pack.version}</span>}</h3>
                      <span className="tag">{pack.type === 'curseforge' ? 'CurseForge' : 'Manuell'}</span>
                    </div>
                    {pack.description && (
                      <p className="text-sm text-muted">{pack.description}</p>
                    )}

                    {pack.mcVersion && (
                      <div className="flex gap-2 mt-2" style={{ flexWrap: 'wrap' }}>
                        <span className="tag">MC {pack.mcVersion}</span>
                        {pack.loaderType && <span className="tag">{pack.loaderType} {pack.loaderVersion}</span>}
                        {pack.recommendedRam && <span className="tag">RAM: {formatRam(pack.recommendedRam)}</span>}
                      </div>
                    )}

                    <div className="mt-2">
                      <p className="text-sm text-muted">
                        {pack.mods?.length || 0} Mods installiert
                        {pack.failedMods > 0 && <span style={{ color: 'var(--danger)' }}> ({pack.failedMods} fehlgeschlagen)</span>}
                      </p>
                    </div>

                    {pack.mods && pack.mods.length > 0 && (
                      <div className="mt-2">
                        <details>
                          <summary className="text-sm text-muted" style={{ cursor: 'pointer' }}>Mod-Liste anzeigen</summary>
                          <div className="flex gap-2 mt-2" style={{ flexWrap: 'wrap', maxHeight: '150px', overflowY: 'auto' }}>
                            {pack.mods.map(mod => (
                              <span key={mod.fileName} className="tag">{mod.fileName}</span>
                            ))}
                          </div>
                        </details>
                      </div>
                    )}

                    {server && (
                      <p className="text-sm mt-2" style={{ color: 'var(--accent)' }}>
                        Server: {server.name} (Port {server.port})
                      </p>
                    )}

                    <div className="text-sm text-muted mt-2">
                      Importiert: {new Date(pack.importedAt || pack.createdAt).toLocaleDateString('de-DE')}
                    </div>
                    <div className="flex gap-2 mt-4">
                      <button className="btn btn-primary btn-sm" onClick={() => handleExport(pack.id)}>
                        Exportieren (ZIP)
                      </button>
                      <button className="btn btn-danger btn-sm" onClick={() => handleDelete(pack.id)}>
                        Loeschen
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {activeTab === 'import' && (
        <div>
          <div className="card">
            <div style={{ marginBottom: 16 }}>
              <p className="text-sm text-muted">
                Lege CurseForge Modpack ZIP-Dateien in <code>/minecraft/upload</code> ab und importiere sie hier.
                Der Import liest die manifest.json und laedt alle Mods automatisch von CurseForge herunter.
              </p>
              <button className="btn btn-secondary btn-sm mt-2" onClick={loadUploads} disabled={importing}>
                Aktualisieren
              </button>
            </div>

            {uploads.length === 0 ? (
              <div className="empty-state">
                <div className="icon">&#128193;</div>
                <p>Keine ZIP-Dateien im Upload-Ordner</p>
                <p className="text-sm text-muted">Lege CurseForge Modpack .zip Dateien in /minecraft/upload ab</p>
              </div>
            ) : (
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Datei</th>
                      <th>Details</th>
                      <th>Aktion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {uploads.map(file => (
                      <tr key={file.fileName}>
                        <td>
                          <div className="fw-bold">{file.fileName}</div>
                          <div className="text-sm text-muted">{formatSize(file.size)}</div>
                        </td>
                        <td>
                          {file.isCurseForge && file.manifest ? (
                            <div>
                              <div className="fw-bold">{file.manifest.name} v{file.manifest.version}</div>
                              <div className="text-sm text-muted">
                                MC {file.manifest.mcVersion} | {file.manifest.modLoader} | {file.manifest.modCount} Mods
                              </div>
                              {file.manifest.author && (
                                <div className="text-sm text-muted">von {file.manifest.author}</div>
                              )}
                            </div>
                          ) : (
                            <span className="text-sm text-muted">Kein CurseForge Modpack</span>
                          )}
                        </td>
                        <td>
                          {file.isCurseForge ? (
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={() => handleImport(file.fileName)}
                              disabled={importing}
                            >
                              {importing ? <span className="spinner" /> : 'Importieren'}
                            </button>
                          ) : (
                            <span className="text-sm text-muted">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Import Progress Log */}
          {importLog.length > 0 && (
            <div className="card mt-4">
              <h3 style={{ marginBottom: 12 }}>Import-Fortschritt</h3>
              {importProgress && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{
                    background: 'var(--bg-tertiary)',
                    borderRadius: 4,
                    height: 24,
                    overflow: 'hidden',
                  }}>
                    <div style={{
                      background: 'var(--accent)',
                      height: '100%',
                      width: `${(importProgress.current / importProgress.total) * 100}%`,
                      transition: 'width 0.3s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 12,
                      fontWeight: 'bold',
                      color: 'var(--bg-primary)',
                    }}>
                      {importProgress.current}/{importProgress.total}
                    </div>
                  </div>
                </div>
              )}
              <div style={{
                maxHeight: 300,
                overflowY: 'auto',
                fontFamily: 'monospace',
                fontSize: 13,
                lineHeight: 1.6,
                padding: '8px 12px',
                background: 'var(--bg-primary)',
                borderRadius: 8,
              }}>
                {importLog.map((entry, i) => (
                  <div key={i} style={{
                    color: entry.step === 'error' ? 'var(--danger)' :
                           entry.step === 'download_error' ? '#ff9800' :
                           entry.step === 'done' ? 'var(--accent)' :
                           'var(--text-secondary)',
                  }}>
                    {entry.message}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default Modpacks;
